import { useTranslation } from "react-i18next";

import { UseQueryResult, useQueries } from "@tanstack/react-query";
import { chunk } from "lodash";

import { getFieldPracticesBulk } from "services";

import { getActiveToken, persistentQueryClient, useUserContext } from "utils";

import { FIELD_PRACTICES_CHUNK_SIZE, GET_FIELDS, GET_FIELD_PRACTICES } from "consts";

import { FarmResponse, FieldListField, FieldPracticeResponse } from "models";

type UseGetAllFieldPracticesByFarmReturnType = Pick<
  UseQueryResult<Record<string, FieldPracticeResponse>>,
  "data" | "isFetching" | "isLoading" | "isError" | "isLoadingError"
>;

export const useGetAllFieldPracticesByFarm = (farm: FarmResponse | undefined): UseGetAllFieldPracticesByFarmReturnType => {
  // Not having { useSuspense: false } caused unintended UI behavior — like a drawer opening and closing on mount,
  // due to remounting triggered by Suspense fallback.
  // DO NOT REMOVE unless you're explicitly handling Suspense with fallback UIs.
  const { t } = useTranslation(["practices"], { useSuspense: false });
  const queryClient = persistentQueryClient;

  const { user } = useUserContext();
  const token = getActiveToken();

  const queryState = queryClient.getQueryState<FieldListField[]>([GET_FIELDS, farm?.id]);
  const fields = queryState?.data ?? [];
  const sortFields = (a: FieldListField, b: FieldListField) => (b.size ?? 0) - (a.size ?? 0);

  const fieldIds = fields?.toSorted(sortFields).map(({ id }) => id) ?? [];
  const chunks = chunk(fieldIds, FIELD_PRACTICES_CHUNK_SIZE);

  const queries = chunks.map((chunk) => ({
    queryKey: [GET_FIELD_PRACTICES, chunk],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const fieldPracticeDataList = await getFieldPracticesBulk({ fieldIds: chunk, signal });
      return fieldPracticeDataList.map((fieldPracticeData) => {
        const cropsWithInjectedNames = fieldPracticeData.crops.map((crop) => ({ ...crop, cropName: t(`crops.${crop.translationSlug}`) }));

        return {
          ...fieldPracticeData,
          crops: cropsWithInjectedNames,
        };
      });
    },
    enabled: !!farm && !!user && !!token && fieldIds.length > 0,
    staleTime: 1000 * 60 * 60 * 24, // 1 day
  }));

  return useQueries({
    queries,
    combine: combineFieldPracticesQueries(!!farm),
  });
};

const combineFieldPracticesQueries = (enabled: boolean) => (results: UseQueryResult<FieldPracticeResponse[], Error>[]) => ({
  data: results.reduce(
    (acc, result) => {
      if (result.data && Array.isArray(result.data)) {
        result.data.forEach((fieldPractice) => {
          acc[fieldPractice.fieldId] = fieldPractice;
        });
      }
      return acc;
    },
    {} as Record<string, FieldPracticeResponse>
  ),
  enabled,
  isLoading: results.some((result) => !!result.isLoading),
  isFetching: results.some((result) => !!result.isFetching),
  isError: results.some((result) => !!result.isError),
  isLoadingError: results.some((result) => !!result.isLoadingError),
});
